import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ResponsiveImage from './ResponsiveImage';

/** Pastel “sticky note” tones — cycle by slide index (desktop row / mobile). */
const NOTE_PALETTE = [
  {
    key: 'yellow',
    bg: '#FFF4C8',
    quote: '#C4A43A',
    text: '#3a3a38',
    shadow: '0 14px 36px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.5)',
  },
  {
    key: 'mint',
    bg: '#C5EDD9',
    quote: '#2d6b52',
    text: '#1d3d32',
    shadow: '0 16px 40px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.45)',
  },
  {
    key: 'coral',
    bg: '#FFD6CC',
    quote: '#b5523f',
    text: '#4a2c28',
    shadow: '0 14px 36px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.45)',
  },
];

const SWIPE_MIN_PX = 48;
/** Mobile: pin lifts slightly, then next slide’s pin settles from that pose (reattach). */
const PIN_LIFT_MS = 260;
const NOTE_DRIFT_MS = 520;
const PIN_REATTACH_MS = 380;

function normalizeReviews(reviews) {
  if (!Array.isArray(reviews)) return [];
  return reviews.filter((r) => {
    if (!r || typeof r !== 'object') return false;
    const q = r.quote != null ? String(r.quote).trim() : '';
    const img = r.image != null ? String(r.image).trim() : '';
    return q.length > 0 || img.length > 0;
  });
}

function paletteForSlide(slide, index) {
  const raw = slide.noteColor && String(slide.noteColor).toLowerCase();
  const byKey = NOTE_PALETTE.find((p) => p.key === raw);
  if (byKey) return byKey;
  return NOTE_PALETTE[index % NOTE_PALETTE.length];
}

/** Mobile-only pinboard tilt — every slide index gets a small angle (cycles for long lists) */
function noteTiltDeg(index) {
  const pattern = [-1, -5, -9, -12, -15];
  return pattern[index % pattern.length];
}

function FoundersPinAsset() {
  return (
    <img
      src="/assets/PIN.svg"
      alt=""
      width={62}
      height={66}
      className="h-[58px] w-auto max-w-[56px] select-none object-contain"
      draggable={false}
      loading="eager"
      decoding="async"
    />
  );
}

/**
 * Pinboard card — pin is a separate layer so it can lift / hand off before the note drifts away.
 * @param {string} [pinClassName] — e.g. lift / handoff / reattach on mobile
 * @param {string} [noteBodyClassName] — e.g. drift animation after pin lifts / hides
 * @param {boolean} [hidePin] — omit pin (e.g. mobile uses one shared pin on the stage)
 */
function PinboardReviewCard({
  quote,
  image,
  palette,
  rotateDeg = 0,
  className = '',
  pinClassName = '',
  noteBodyClassName = '',
  hidePin = false,
  imagePriority = false,
}) {
  const hasImage = Boolean(image && String(image).trim());

  return (
    <div className={`relative flex justify-center ${className}`}>
      <div
        className="relative w-full max-w-[350px]"
        style={{
          transform: `rotate(${rotateDeg}deg)`,
          transformOrigin: 'center center',
        }}
      >
        <div
          className={`relative overflow-visible px-[24px] flex justify-start gap-[20px] pt-[40px] h-[350px] ${noteBodyClassName}`}
          style={{
            backgroundColor: palette.bg,
            boxShadow: palette.shadow,
            flexDirection: 'column',
          }}
        >
          <img
            className="pointer-events-none w-[30px] h-[30px] opacity-[0.1]"
            style={{ color: palette.quote }}
            src="/assets/quote.svg"
            aria-hidden
          />
        

          <p
            className="relative z-10  text-[18px] font-medium leading-[1.1]"
            style={{ color: palette.text }}
          >
            {quote || '\u00A0'}
          </p>

          {hasImage ? (
            <div className="pointer-events-none absolute bottom-0 left-[24px] z-20 w-[80%] max-w-[300px] translate-y-[40%]">
              <div className="pointer-events-auto shadow-[0_10px_28px_rgba(0,0,0,0.22)]">
                <div className="aspect-square w-full overflow-hidden">
                  <ResponsiveImage
                    src={image}
                    variant="founderSquare"
                    alt=""
                    className="h-full w-full object-cover object-center"
                    priority={imagePriority}
                    loading={imagePriority ? 'eager' : 'lazy'}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {hidePin ? null : (
          <div
            className={`pointer-events-none absolute left-1/2 top-0 z-30 -translate-x-1/2 -translate-y-[42%] drop-shadow-md ${pinClassName}`}
          >
            <FoundersPinAsset />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Founders’ review pinboard — desktop: row of notes; mobile: one shared pin, lift → note drifts → pin settles (reattach).
 */
export default function FoundersReviewCarousel({ reviews }) {
  const slides = useMemo(() => normalizeReviews(reviews), [reviews]);
  const [visibleIndex, setVisibleIndex] = useState(0);
  /** Mobile transition: idle → pin lifts slightly → note falls away (next pin settles) */
  const [transitionPhase, setTransitionPhase] = useState('idle');
  const pendingTarget = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const n = slides.length;
  const safeIndex = n === 0 ? 0 : Math.min(visibleIndex, n - 1);

  useEffect(() => {
    if (n === 0) return;
    setVisibleIndex((v) => Math.min(v, n - 1));
  }, [n]);

  // Warm browser cache for all slide photos (avoids flash when swipe changes index).
  useEffect(() => {
    slides.forEach((slide) => {
      const url = slide.image && String(slide.image).trim();
      if (!url) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  }, [slides]);

  const navigateTo = useCallback(
    (target) => {
      if (n <= 1 || target === safeIndex) return;
      if (transitionPhase !== 'idle') return;
      pendingTarget.current = target;
      setTransitionPhase('pin');
    },
    [n, safeIndex, transitionPhase]
  );

  useEffect(() => {
    if (transitionPhase !== 'pin') return undefined;
    const t = window.setTimeout(() => setTransitionPhase('note'), PIN_LIFT_MS);
    return () => window.clearTimeout(t);
  }, [transitionPhase]);

  useEffect(() => {
    if (transitionPhase !== 'note') return undefined;
    const t = window.setTimeout(() => {
      setVisibleIndex(pendingTarget.current);
      setTransitionPhase('idle');
    }, NOTE_DRIFT_MS);
    return () => window.clearTimeout(t);
  }, [transitionPhase]);

  const go = useCallback(
    (dir) => {
      if (n <= 1) return;
      const next = (safeIndex + dir + n) % n;
      navigateTo(next);
    },
    [n, safeIndex, navigateTo]
  );

  const onTouchStart = useCallback((e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  }, []);

  const onTouchMove = useCallback((e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (touchStartX.current == null || touchEndX.current == null || n <= 1) return;
    const delta = touchStartX.current - touchEndX.current;
    if (delta > SWIPE_MIN_PX) go(1);
    else if (delta < -SWIPE_MIN_PX) go(-1);
  }, [n, go]);

  if (n === 0) return null;

  const transitionTarget =
    (transitionPhase === 'pin' || transitionPhase === 'note') && pendingTarget.current != null
      ? pendingTarget.current
      : null;

  const pinPulling = transitionPhase === 'pin';
  const noteDrifting = transitionPhase === 'note';

  return (
    <section className="bg-[#FAF7F2] px-0 py-12 md:px-16 md:py-16 lg:px-[120px] h-[600px] md:h-auto md:min-h-[480px] md:pb-40 md:pt-4">
      <style>{`
        /* Detach: same anchor as idle (–50%, –42%); only scale/rotate — no sliding the pin */
        @keyframes founders-pin-lift {
          0% {
            transform: translate(-50%, -42%) scale(1) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -42%) scale(1.08) rotate(-6deg);
            opacity: 1;
          }
        }
        .founders-pin-lift {
          animation: founders-pin-lift ${PIN_LIFT_MS}ms cubic-bezier(0.45, 0, 0.2, 1) forwards;
          will-change: transform;
        }
        @keyframes founders-pin-reattach {
          0% {
            transform: translate(-50%, -42%) scale(1.08) rotate(-6deg);
            opacity: 1;
          }
          72% {
            transform: translate(-50%, -42%) scale(0.96) rotate(2deg);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -42%) scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        .founders-pin-reattach {
          animation: founders-pin-reattach ${PIN_REATTACH_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
          will-change: transform;
        }
        @keyframes founders-note-drift-away {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(105%) rotate(11deg);
            opacity: 0;
          }
        }
        .founders-note-drift-away {
          animation: founders-note-drift-away ${NOTE_DRIFT_MS}ms cubic-bezier(0.45, 0.05, 0.55, 0.95) forwards;
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .founders-pin-lift,
          .founders-pin-reattach,
          .founders-note-drift-away {
            animation: none !important;
          }
          .founders-note-drift-away {
            opacity: 1 !important;
            transform: none !important;
          }
          .founders-pin-lift {
            opacity: 1 !important;
            transform: none !important;
          }
          .founders-pin-reattach {
            opacity: 1 !important;
            transform: none !important;
          }
        }
        .founders-mobile-stage {
          perspective: 1400px;
          transform-style: preserve-3d;
        }
      `}</style>

      {/* Desktop: single row, horizontal scroll (photos extend below — extra section pb) */}
      <div className="hidden md:block md:-mx-4 md:px-4 lg:-mx-8 lg:px-8">
        <div
          className="flex flex-row flex-nowrap items-start justify-start gap-x-10 overflow-x-auto pb-28 pt-16 scrollbar-hide scroll-smooth snap-x snap-mandatory lg:gap-x-14"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {slides.map((slide, i) => {
            const pal = paletteForSlide(slide, i);
            return (
              <PinboardReviewCard
                key={`${i}-${slide.image || ''}-${String(slide.quote).slice(0, 20)}`}
                quote={slide.quote}
                image={slide.image}
                palette={pal}
                rotateDeg={0}
                className="mb-4 shrink-0 snap-start md:mb-0"
              />
            );
          })}
        </div>
      </div>

      {/* Mobile: all slides stay mounted — stable keys prevent image reload on swipe. */}
      <div className="md:hidden">
        <div
          className="founders-mobile-stage relative min-h-[380px] max-w-full pb-28 pt-6"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {slides.map((slide, i) => {
            const pal = paletteForSlide(slide, i);
            const isFront = i === safeIndex;
            const isUnder = transitionTarget === i;
            const isVisible = isFront || isUnder;

            return (
              <div
                key={`founder-slide-${i}`}
                className={
                  isVisible
                    ? isFront
                      ? 'relative z-10 mx-auto w-full'
                      : 'pointer-events-none absolute left-1/2 top-0 z-[1] w-full'
                    : 'pointer-events-none absolute left-1/2 top-0 z-0 w-full -translate-x-1/2 opacity-0'
                }
                style={
                  isUnder
                    ? {
                        transform: 'translateX(-50%) translateY(20px) scale(1)',
                        transformOrigin: 'center center',
                      }
                    : undefined
                }
                aria-hidden={!isVisible || isUnder}
              >
                <div
                  className="relative mx-auto w-full"
                  style={{
                    transform: `rotate(${noteTiltDeg(isFront ? safeIndex : i)}deg)`,
                    transformOrigin: 'center center',
                  }}
                >
                  <PinboardReviewCard
                    hidePin
                    rotateDeg={0}
                    quote={slide.quote}
                    image={slide.image}
                    palette={pal}
                    imagePriority
                    noteBodyClassName={isFront && noteDrifting ? 'founders-note-drift-away' : ''}
                  />
                  {isFront ? (
                    <div
                      className={`pointer-events-none absolute left-1/2 top-0 z-30 -translate-x-1/2 -translate-y-[42%] drop-shadow-md ${
                        pinPulling ? 'founders-pin-lift' : noteDrifting ? 'founders-pin-reattach' : ''
                      }`}
                    >
                      <FoundersPinAsset />
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {n > 1 ? (
          <div className="mt-4 flex items-center justify-center gap-[6px] px-4">
            <div className="flex items-center gap-1.5">
              {slides.slice(0, safeIndex).map((_, dotIdx) => (
                <button
                  key={`L-${dotIdx}`}
                  type="button"
                  aria-label={`Review ${dotIdx + 1}`}
                  onClick={() => navigateTo(dotIdx)}
                  disabled={transitionPhase !== 'idle'}
                  className="h-[6px] w-[6px] rounded-full bg-[#ffe1d1] transition hover:bg-[#e8a8a8] disabled:opacity-40"
                />
              ))}
            </div>
            <div className="inline-flex min-w-[34px] shrink-0 items-center justify-center rounded-full bg-black p-[8px] text-[12px] leading-[12px] font-medium text-white shadow-sm">
              {safeIndex + 1}/{n}
            </div>
            <div className="flex items-center gap-1.5">
              {slides.slice(safeIndex + 1).map((_, i) => {
                const dotIdx = safeIndex + 1 + i;
                return (
                  <button
                    key={`R-${dotIdx}`}
                    type="button"
                    aria-label={`Review ${dotIdx + 1}`}
                    onClick={() => navigateTo(dotIdx)}
                    disabled={transitionPhase !== 'idle'}
                    className="h-[6px] w-[6px] rounded-full bg-[#ffe1d1] transition hover:bg-[#e8a8a8] disabled:opacity-40"
                  />
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
